/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving depots.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { useState, useEffect } from 'react';

import { bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */

import reducerRegistry from '../../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */

import * as Dtos from '../../../api/Neon.Dtos';

import client from '../../../api/client';


/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialDepotsState {
    depots: Dtos.Depot[] | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
};

export interface IDepotsState {
    depots: IInitialDepotsState;
};

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const DepotsInitialState: IInitialDepotsState = {
    depots: null,
    loadState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useDepotsSelector: TypedUseSelectorHook<IDepotsState> = useSelector;

export const DepotsSelectors = {
    selectDepots: (state: IDepotsState) => state.depots.depots,
    selectLoadState: (state: IDepotsState) => state.depots.loadState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class DepotsReducer extends ImmerReducer<IInitialDepotsState> {
    public load() {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(depots?: Dtos.Depot[]) {
        this.draftState.depots = depots ? depots : null;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.depots = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState = { ...DepotsInitialState };
    }
};

export const DepotsActions = createActionCreators(DepotsReducer);
const reducer = createReducerFunction(DepotsReducer, DepotsInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const DepotsApi = {
    load: () => {
        return client.get(new Dtos.GetDepots());
    }
};

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const DepotsLogic = {
    load: createLogic<IDepotsState, {}, undefined, string, ReturnType<typeof DepotsActions.load>>({
        type: DepotsActions.load.type,
        process: async ({ action }, dispatch, done) => {
            try {
                const response = await DepotsApi.load();

                dispatch(DepotsActions.loadSuccess(
                    response.depots
                ));
            }
            catch (error: any) {
                dispatch(DepotsActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    DepotsLogic.load
];

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const useDepots = (): [Dtos.Depot[] | null, IRequestState<Dtos.ResponseStatus>, typeof DepotsActions] => {
    const depots = useSelector(DepotsSelectors.selectDepots);
    const loadState = useSelector(DepotsSelectors.selectLoadState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof DepotsActions>(() => {
        return bindActionCreators(DepotsActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(DepotsActions, dispatch));
    }, [dispatch, setActions]);

    return [
        depots,
        loadState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('depots', reducer as any, logics as any);